/*----------------------------------
- DOMAINS FILM
----------------------------------*/

/**
 * The product film under the closing block, as the mock draws it (`dm-film`): the kit `WindowFrame` with its small
 * bar, the redesigned Browse (`assets/demo/browse.webp`) as the poster, and "Play animation" at the bottom right (a
 * round button below 640px), which loads and plays the existing 29-second video on the press only. The shipped
 * poster showed the retired product (mock head comment, "Deviations from shipped").
 *
 * The poster is decorative (`alt=""`): the caption, kept for screen readers as shipped, says what the film shows.
 */

/*----------------------------------
- DEPENDANCES
----------------------------------*/

import { Button } from '@uniquedomains/components/kit/button/Button';
import { IconButton } from '@uniquedomains/components/kit/icon-button/IconButton';
import { WindowFrame } from '@uniquedomains/components/kit/window-frame/WindowFrame';

import BrowsePoster from '@/client/assets/demo/browse.webp';
import Icon from '@/client/components/ui/icon/Icon';
import { DOMAINS_FILM_COPY } from '@/client/catalogs/landing/domains/pageCopy';
import { SCREENER_FILM_COPY } from '@/client/catalogs/landing/product/pageChrome';
import { HOME_SCREENER_BLUEPRINT } from '@/common/catalogs/landing/homeScreenerBlueprint';
import useDomainsFilm from '../lib/useDomainsFilm';

/*----------------------------------
- CONSTANTS
----------------------------------*/

/** The poster's own size (1920 x 1080), so the box is reserved before it loads. */
const POSTER_SIZE = { width: 1920, height: 1080 } as const;

/*----------------------------------
- COMPONENT
----------------------------------*/

const DomainsFilm = () => {
    const { variant, isPlaying, showVideo, failed, videoRef, toggle, onPlaying, onPause, onError } = useDomainsFilm();
    const media = variant ? HOME_SCREENER_BLUEPRINT.variants[variant] : null;
    const videoId = `${HOME_SCREENER_BLUEPRINT.id}-domains`;
    const label = failed ? SCREENER_FILM_COPY.unavailable : isPlaying ? SCREENER_FILM_COPY.pause : SCREENER_FILM_COPY.play;
    const icon = <Icon name={isPlaying ? 'pause' : 'play'} size={16} strokeWidth={2} />;
    // The walkthrough tooltip describes the Play action only: Pause and Unavailable carry no such promise (review m-22).
    const title = !failed && !isPlaying ? DOMAINS_FILM_COPY.playTitle : undefined;

    return (
        <section class="dm-film">
            <figure class="dm-film__fig">
                <WindowFrame bar="sm">
                    <div class="dm-film__media" data-video-visible={showVideo ? 'true' : 'false'}>
                        <picture>
                            <img src={BrowsePoster} width={POSTER_SIZE.width} height={POSTER_SIZE.height} alt="" loading="lazy" decoding="async" />
                        </picture>
                        {media ? (
                            <video
                                ref={videoRef}
                                id={videoId}
                                class="dm-film__video"
                                width={media.videoWidth}
                                height={media.videoHeight}
                                muted
                                playsInline
                                autoPlay
                                preload="auto"
                                aria-hidden="true"
                                tabIndex={-1}
                                onPlaying={onPlaying}
                                onPause={onPause}
                                onError={onError}
                            >
                                <source src={media.webmPath} type="video/webm" />
                                <source src={media.mp4Path} type="video/mp4" />
                            </video>
                        ) : null}
                        <span class="dm-play dm-play--lg">
                            <Button variant="soft" size="md" icon={icon} title={title} aria-controls={media ? videoId : undefined} disabled={failed} onClick={toggle}>
                                {label}
                            </Button>
                        </span>
                        <span class="dm-play dm-play--sm">
                            <IconButton size="xl" label={label} title={title} icon={icon} aria-controls={media ? videoId : undefined} disabled={failed} onClick={toggle} />
                        </span>
                    </div>
                </WindowFrame>
                <figcaption class="ud-sr-only">{HOME_SCREENER_BLUEPRINT.caption}</figcaption>
            </figure>
        </section>
    );
};

export default DomainsFilm;
